import { useMutation, useQueryClient } from '@tanstack/react-query';

import { SAVED_CLASSES_QUERY_KEY } from './useGetSavedClasses';

interface SaveClassVariables {
  liveTrainingId: string;
  isSaved: boolean;
}

interface SavedClassItem {
  contentfulId: string | null;
  liveTrainingId: string;
  contentType: string;
  savedDate: number;
  learningPathContentfulId: string | null;
}

interface SavedClassesResponse {
  savedIds: SavedClassItem[];
  totalCount: number;
}

/**
 * Custom hook to save or unsave a class
 * @returns Mutation for saving/unsaving a class
 *
 * @example
 * const { mutate: saveClass, isPending } = useSaveClass();
 * saveClass({ liveTrainingId: 'course:14838775', isSaved: true });
 */
export const useSaveClass = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ liveTrainingId, isSaved }: SaveClassVariables) => {
      const response = await fetch('/gateway/api/learning-app-progress-fe/my-learning/is-saved', {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          isSaved,
          contentType: 'LIVE_TRAINING',
          liveTrainingId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${isSaved ? 'save' : 'unsave'} class`);
      }

      return { success: true, liveTrainingId, isSaved };
    },
    onSuccess: (_data, { liveTrainingId, isSaved }) => {
      // Optimistically update the cache immediately
      queryClient.setQueryData<SavedClassesResponse>([SAVED_CLASSES_QUERY_KEY], (oldData) => {
        if (!oldData) {
          return oldData;
        }

        if (isSaved) {
          // Add the class to saved list
          const newItem: SavedClassItem = {
            contentfulId: null,
            liveTrainingId,
            contentType: 'LIVE_TRAINING',
            savedDate: Date.now() / 1000,
            learningPathContentfulId: null,
          };

          return {
            savedIds: [...oldData.savedIds, newItem],
            totalCount: oldData.totalCount + 1,
          };
        }

        // Remove the class from saved list
        return {
          savedIds: oldData.savedIds.filter((item) => item.liveTrainingId !== liveTrainingId),
          totalCount: oldData.totalCount - 1,
        };
      });
    },
  });
};

